<template>
  <div class="admin-layout">
    <!-- Top Navigation -->
    <nav class="navbar navbar-expand-lg navbar-dark bg-primary fixed-top">
      <div class="container-fluid">
        <!-- Sidebar Toggle -->
        <button
          class="btn btn-outline-light me-3"
          @click="toggleSidebar"
          type="button"
        >
          <i class="fas fa-bars"></i>
        </button>

        <!-- Brand -->
        <router-link class="navbar-brand" to="/admin/dashboard">
          <i class="fas fa-route me-2"></i>
          Gypsy Souls Admin
        </router-link>

        <!-- User Menu -->
        <div class="navbar-nav ms-auto">
          <div class="nav-item dropdown">
            <a
              class="nav-link dropdown-toggle"
              href="#"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i class="fas fa-user-circle me-2"></i>
              {{ currentAdmin?.email }}
            </a>
            <ul class="dropdown-menu dropdown-menu-end">
              <li>
                <button class="dropdown-item" @click="logout">
                  <i class="fas fa-sign-out-alt me-2"></i>
                  Logout
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </nav>

    <!-- Sidebar -->
    <aside class="sidebar" :class="{ collapsed: isSidebarCollapsed }">
      <nav class="sidebar-nav">
        <ul class="sidebar-menu">
          <li>
            <router-link to="/admin/dashboard" class="sidebar-link">
              <i class="fas fa-tachometer-alt"></i>
              <span>Dashboard</span>
            </router-link>
          </li>
          <li>
            <router-link to="/admin/trips" class="sidebar-link">
              <i class="fas fa-map-marked-alt"></i>
              <span>Trips</span>
            </router-link>
          </li>
          <li>
            <router-link to="/admin/reviews" class="sidebar-link">
              <i class="fas fa-star"></i>
              <span>Reviews</span>
            </router-link>
          </li>
          <li>
            <router-link to="/admin/users" class="sidebar-link">
              <i class="fas fa-users"></i>
              <span>Users</span>
            </router-link>
          </li>
          <li>
            <a href="/trips" target="_blank" class="sidebar-link">
              <i class="fas fa-external-link-alt"></i>
              <span>View Public Site</span>
            </a>
          </li>
        </ul>
      </nav>
    </aside>

    <!-- Main Content -->
    <main class="main-content" :class="{ expanded: isSidebarCollapsed }">
      <!-- Loading Overlay -->
      <div v-if="isLoading" class="loading-overlay">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Page Content -->
      <div class="content-wrapper">
        <slot />
      </div>
    </main>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Swal from "sweetalert2";

export default {
  name: "AppLayout",
  computed: {
    ...mapGetters(["isLoading", "isSidebarCollapsed"]),
    ...mapGetters("auth", ["currentAdmin"]),
  },
  methods: {
    ...mapActions(["toggleSidebar"]),
    ...mapActions("auth", ["logout"]),

    async logout() {
      try {
        const result = await Swal.fire({
          title: "Logout Confirmation",
          text: "Are you sure you want to logout?",
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#2c5aa0",
          cancelButtonColor: "#6c757d",
          confirmButtonText: "Yes, logout",
          cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
          this.$store.dispatch("auth/logout");
          this.$router.push("/login");

          Swal.fire({
            title: "Logged Out",
            text: "You have been successfully logged out.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        console.error("Logout error:", error);
      }
    },
  },
};
</script>

<style scoped>
.admin-layout {
  height: 100vh;
  overflow: hidden;
}

.navbar {
  z-index: 1030;
}

.sidebar {
  background: var(--primary-color);
  color: white;
  height: calc(100vh - 56px);
  position: fixed;
  left: 0;
  top: 56px;
  width: 250px;
  overflow-y: auto;
  transition: all 0.3s ease;
  z-index: 1020;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-nav {
  padding: 1rem 0;
}

.sidebar-menu {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sidebar-link {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
}

.sidebar-link:hover,
.sidebar-link.router-link-active {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.sidebar-link i {
  width: 20px;
  margin-right: 1rem;
  text-align: center;
}

.sidebar.collapsed .sidebar-link span {
  display: none;
}

.main-content {
  margin-left: 250px;
  padding-top: 56px;
  height: 100vh;
  overflow-y: auto;
  transition: all 0.3s ease;
  position: relative;
}

.main-content.expanded {
  margin-left: 70px;
}

.content-wrapper {
  padding: 2rem;
  min-height: calc(100vh - 56px);
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }

  .sidebar.show {
    transform: translateX(0);
  }

  .main-content {
    margin-left: 0;
  }

  .main-content.expanded {
    margin-left: 0;
  }

  .content-wrapper {
    padding: 1rem;
  }
}
</style>
